import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Users, TrendingUp, Award, Heart, Star, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../../services/dataSync';

const CoreValuesPage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const coreValues = [
    {
      icon: Shield,
      title: getText('about.values.integrity'),
      description: getText('about.values.integrity.desc'),
      details: [
        getText('about.values.integrity.details.1'),
        getText('about.values.integrity.details.2'),
        getText('about.values.integrity.details.3'),
        getText('about.values.integrity.details.4')
      ],
      theme: { 
        border: 'border-blue-400', 
        badge: 'bg-gradient-to-br from-blue-500 to-blue-700', 
        hover: 'group-hover:text-blue-600',
        bg: 'from-blue-50 to-blue-100'
      }
    },
    {
      icon: Users,
      title: getText('about.values.collaboration'),
      description: getText('about.values.collaboration.desc'),
      details: [
        getText('about.values.collaboration.details.1'),
        getText('about.values.collaboration.details.2'),
        getText('about.values.collaboration.details.3'),
        getText('about.values.collaboration.details.4')
      ],
      theme: { 
        border: 'border-emerald-400', 
        badge: 'bg-gradient-to-br from-emerald-500 to-emerald-700', 
        hover: 'group-hover:text-emerald-600',
        bg: 'from-emerald-50 to-emerald-100'
      }
    },
    {
      icon: TrendingUp,
      title: getText('about.values.innovation'),
      description: getText('about.values.innovation.desc'),
      details: [
        getText('about.values.innovation.details.1'),
        getText('about.values.innovation.details.2'),
        getText('about.values.innovation.details.3'),
        getText('about.values.innovation.details.4')
      ],
      theme: { 
        border: 'border-purple-400', 
        badge: 'bg-gradient-to-br from-purple-500 to-purple-700', 
        hover: 'group-hover:text-purple-600',
        bg: 'from-purple-50 to-purple-100'
      }
    },
    {
      icon: Award,
      title: getText('about.values.excellence'),
      description: getText('about.values.excellence.desc'),
      details: [
        getText('about.values.excellence.details.1'),
        getText('about.values.excellence.details.2'),
        getText('about.values.excellence.details.3'),
        getText('about.values.excellence.details.4')
      ],
      theme: { 
        border: 'border-amber-400', 
        badge: 'bg-gradient-to-br from-amber-500 to-amber-700', 
        hover: 'group-hover:text-amber-600',
        bg: 'from-amber-50 to-amber-100'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* About Us Button - Top */}
            <div className="mb-4">
              <Link to="/about" className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg">
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">{getText('nav.about.submenu.about')}</span>
              </Link>
            </div>
            
            <div className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight">
                {getText('about.values.title')}
              </h1>
              <p className="text-lg md:text-xl text-blue-100 font-medium">
                {getText('about.values.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Grid */}
      <section className="section py-16 bg-white">
        <div className="container-medium">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              const theme = value.theme;
              return (
                <div key={index} className="group">
                  <div className={`bg-gradient-to-br ${theme.bg} rounded-2xl p-6 md:p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 ${theme.border} h-full flex flex-col`}>
                    <div className={`${theme.badge} rounded-2xl w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-4 md:mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <Icon className="h-8 w-8 md:h-10 md:w-10 text-white" />
                    </div>
                    <h3 className={`text-lg md:text-xl font-bold text-gray-900 mb-3 ${theme.hover} transition-colors`}>{value.title}</h3>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed flex-grow">{value.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Values Section */}
      <section className="section py-16 md:py-20 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="container-medium">
          <div className="section-header mb-12">
            <h2 className="section-title">{getText('about.values.living.title')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getText('about.values.living.desc')}
            </p>
          </div>
          
          <div className="space-y-8 md:space-y-12">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              const theme = value.theme;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                  <div className="flex flex-col lg:flex-row gap-6 md:gap-8 lg:gap-10 items-start">
                    <div className="lg:w-1/3 w-full">
                      <div className={`${theme.badge} rounded-2xl w-20 h-20 md:w-24 md:h-24 flex items-center justify-center mx-auto lg:mx-0 mb-4 md:mb-5 shadow-lg`}>
                        <Icon className="h-10 w-10 md:h-12 md:w-12 text-white" />
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center lg:text-left mb-3">{value.title}</h3>
                      <p className="text-gray-600 text-sm md:text-base text-center lg:text-left leading-relaxed">{value.description}</p>
                    </div>
                    <div className="lg:w-2/3 w-full">
                      <h4 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 md:mb-6">{getText('about.values.initiatives.title')}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        {value.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 text-sm md:text-base leading-relaxed">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values in Action */}
      <section className="section py-16 md:py-20 bg-white">
        <div className="container-medium">
          <div className="section-header mb-12">
            <h2 className="section-title">{getText('about.values.action.title')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getText('about.values.action.desc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="group">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 md:p-8 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 h-full border border-blue-200">
                <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-4 md:mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Heart className="h-8 w-8 md:h-10 md:w-10 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-blue-600 transition-colors">{getText('about.values.action.transparent.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {getText('about.values.action.transparent.desc')}
                </p>
              </div>
            </div>

            <div className="group">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 md:p-8 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 h-full border border-emerald-200">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-4 md:mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Users className="h-8 w-8 md:h-10 md:w-10 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-emerald-600 transition-colors">{getText('about.values.action.community.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {getText('about.values.action.community.desc')}
                </p>
              </div>
            </div>

            <div className="group">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 md:p-8 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 h-full border border-purple-200">
                <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-4 md:mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Star className="h-8 w-8 md:h-10 md:w-10 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-purple-600 transition-colors">{getText('about.values.action.excellence.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {getText('about.values.action.excellence.desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default CoreValuesPage;
